import { Button, Card } from "@rumbledirect/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { message, Modal, Typography, Upload, type UploadProps } from "antd";
import { useState } from "react";
import { useViewConfigurationClient } from "../client";
import {
  Card as V2Card,
  CardHeader,
  CardTitle,
  CardContent,
  FileDropzone,
} from "@rumbledirect/ui-v2";

export interface CalculationUploadCardProps {
  title: string;
  uiV2?: boolean;
  buttonText: string;
  submitEndpoint: string;
  dossierId: string;
  calculation: {
    id: string;
    approved: boolean;
    declined: boolean;
    declinedComment: string | null;
  } | null;
}

const { Dragger } = Upload;

export const CalculationUploadCard = ({
  title,
  buttonText,
  submitEndpoint,
  dossierId,
  calculation,
  uiV2,
}: CalculationUploadCardProps) => {
  const axiosInstance = useViewConfigurationClient();
  const queryClient = useQueryClient();
  const [modalVisible, setModalVisible] = useState(false);

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (
        uiV2 &&
        (!file.name.toLowerCase().endsWith(".txt") ||
          file.size > 10 * 1024 * 1024)
      )
        throw new Error(
          "Alleen Audatex .txt bestanden tot 10 MB zijn toegestaan.",
        );
      const formData = new FormData();
      formData.append("file", file);

      const mediaResponse = await axiosInstance.post<{ id: string }>(
        "/media",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      await axiosInstance.post(submitEndpoint, {
        dossier: dossierId,
        calculationType: "audatex",
        calculationIntent: "calculatie",
        calculationMedia: mediaResponse.data.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["view-configuration"] });
      setModalVisible(false);
    },
    onError: () => {
      if (uiV2) return;
      message.error(
        "Er is een fout opgetreden bij het uploaden van de calculatie.",
      );
    },
  });

  const handleBeforeUpload: UploadProps["beforeUpload"] = (file) => {
    const isTextFile =
      file.type === "text/plain" || file.name.toLowerCase().endsWith(".txt");

    if (!isTextFile) {
      message.error("Alleen .txt bestanden worden geaccepteerd.");
      return Upload.LIST_IGNORE;
    }

    uploadMutation.mutate(file);
    return false;
  };

  const canUpload = calculation === null || calculation.declined;
  if (uiV2) {
    const content = (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {calculation === null
            ? "Upload de Audatex calculatie als .txt bestand."
            : calculation.approved
              ? "De calculatie is goedgekeurd."
              : calculation.declined
                ? `De calculatie is afgekeurd${calculation.declinedComment ? `: ${calculation.declinedComment}` : "."} Upload een nieuwe calculatie.`
                : "Calculatie ontvangen. De calculatie wordt beoordeeld door Check."}
        </p>
        {canUpload && (
          <FileDropzone
            label={buttonText}
            accept=".txt"
            maxSize={10 * 1024 * 1024}
            loading={uploadMutation.isPending}
            onFileSelected={uploadMutation.mutate}
          />
        )}
        {uploadMutation.isError && (
          <p role="alert" className="text-sm text-destructive">
            De calculatie kon niet worden opgeslagen. Probeer het opnieuw.
          </p>
        )}
      </div>
    );
    return (
      <V2Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>{content}</CardContent>
      </V2Card>
    );
  }

  const renderStatus = () => {
    if (calculation === null) {
      return (
        <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
          Nog geen calculatie geüpload.
        </Typography.Paragraph>
      );
    }

    if (calculation.approved) {
      return (
        <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
          De calculatie is goedgekeurd.
        </Typography.Paragraph>
      );
    }

    if (calculation.declined) {
      return (
        <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
          De calculatie is afgekeurd
          {calculation.declinedComment
            ? `: ${calculation.declinedComment}`
            : "."}{" "}
          Upload een nieuwe calculatie.
        </Typography.Paragraph>
      );
    }

    return (
      <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
        De calculatie is geüpload. Wacht op terugkoppeling of deze wordt
        goedgekeurd of afgekeurd.
      </Typography.Paragraph>
    );
  };

  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{title}</span>
          {canUpload && (
            <Button
              type="link"
              style={{ padding: 0, fontWeight: "normal" }}
              onClick={() => setModalVisible(true)}
            >
              {buttonText}
            </Button>
          )}
        </div>
      }
    >
      {renderStatus()}

      <Modal
        title={buttonText}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <Dragger
          multiple={false}
          showUploadList={false}
          accept=".txt,text/plain"
          beforeUpload={handleBeforeUpload}
          disabled={uploadMutation.isPending}
        >
          <p className="ant-upload-text">
            {uploadMutation.isPending
              ? "Bezig met uploaden..."
              : "Klik of sleep de calculatie (.txt) hierheen om te uploaden"}
          </p>
          <p className="ant-upload-hint">
            Alleen .txt bestanden worden geaccepteerd.
          </p>
        </Dragger>
      </Modal>
    </Card>
  );
};
